// Maps Redux actions to conversions and sends them to every configured ad
// platform. Each tag is injected only on
// advertising consent (see index.html), so without consent the globals are
// undefined and these calls are a no-op.
import config from "console_config";
import logException from "Libs/logger";

import { CONVERSION_ACTIONS, type ConversionName } from "./conversions";
import { googleAds } from "./platforms/googleAds";
import { linkedIn } from "./platforms/linkedIn";
import { reddit } from "./platforms/reddit";
import type { AccountIdKey, AdPlatform, PlatformKey } from "./platforms/types";

// Adding a platform: a file in `platforms/` and an entry here, plus its keys in
// `PlatformKey`/`AccountIdKey`, an `ID_*` and `AD_CONVERSIONS` block in each
// vendor config, a loader in index.html, and its endpoints in csp.config.js.
const PLATFORMS: AdPlatform[] = [googleAds, linkedIn, reddit];

// `console_config` is generated per vendor and only carries the keys that
// vendor defines, so the ad keys are read through an explicit optional shape.
type AdConfig = Partial<Record<AccountIdKey, string | false>> & {
  AD_CONVERSIONS?: Partial<
    Record<PlatformKey, Partial<Record<ConversionName, string>>>
  >;
};

const adConfig = config as AdConfig;

/**
 * Sends one conversion to every configured ad platform.
 */
export const trackConversion = (conversion: ConversionName) => {
  for (const platform of PLATFORMS) {
    const accountId = adConfig[platform.accountIdKey];
    const token = adConfig.AD_CONVERSIONS?.[platform.key]?.[conversion];
    if (!accountId || !token) {
      continue;
    }

    try {
      platform.send(token, accountId);
    } catch (error) {
      // A throwing third-party tag must not stop the remaining platforms.
      logException(error, { conversion, platform: platform.name });
    }
  }
};

export default function adConversionTracker(action: { type: string }) {
  const conversion = CONVERSION_ACTIONS[action.type];
  if (conversion) {
    trackConversion(conversion);
  }
}
